import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import { RefreshCw, ChevronLeft, ChevronRight, ArrowRightLeft, Send, Loader2 } from 'lucide-react';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);
const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const COL_DOT = {
  new: 'bg-blue-400', review: 'bg-amber-400', invoice_sent: 'bg-white',
  deposit_paid: 'bg-orange-400', paid_full: 'bg-emerald-400', completed: 'bg-zinc-600', lost: 'bg-red-400',
};

const COL_HINT = {
  new: 'Fresh inquiries — review, then build their invoice',
  review: 'Being reviewed / vendors confirming',
  invoice_sent: 'Invoice is with the client, waiting on deposit',
  deposit_paid: 'Deposit in — date is reserved',
  paid_full: 'Fully paid, ready for event day',
  completed: 'Event done — review request goes out',
  lost: 'Didn\'t book',
};

function PipelineCard({ card, currentCol, onMoveRequest, onOpen }) {
  let age = null;
  if (currentCol === 'new' && card.created_at) {
    const h = (Date.now() - new Date(card.created_at).getTime()) / 36e5;
    const label = h < 1 ? `${Math.max(1, Math.round(h * 60))}m` : h < 24 ? `${Math.round(h)}h` : `${Math.floor(h / 24)}d`;
    const cls = h < 2 ? 'text-zinc-500 bg-white/5' : h < 24 ? 'text-amber-400 bg-amber-400/10' : 'text-red-400 bg-red-400/10';
    age = { label, cls };
  }
  return (
    <div
      className="bg-[#161616] border border-white/5 rounded-2xl px-4 py-4 cursor-pointer active:scale-[0.98] transition-transform"
      onClick={() => onOpen && onOpen(card.proposal_id)}
      data-testid={`pipeline-card-${card.proposal_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white text-[16px] font-bold tracking-tight truncate" style={FONT_H}>{card.client_name || 'Unknown'}</p>
          <p className="text-zinc-500 text-[13px] mt-1 truncate" style={FONT_B}>
            {card.event_type || 'Event'}{card.event_date ? ` · ${card.event_date}` : ''}
            {age && <span className={`ml-2 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide ${age.cls}`} data-testid={`wait-${card.proposal_id}`}>waiting {age.label}</span>}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-white text-[15px] font-bold tabular-nums" style={FONT_H}>{fmt(card.total)}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onMoveRequest(card, currentCol); }}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 active:scale-95 transition-transform"
            data-testid={`move-${card.proposal_id}`}
            aria-label="Move stage"
          >
            <ArrowRightLeft className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MoveSheet({ move, columns, onClose, onMove }) {
  const [confirmInvoice, setConfirmInvoice] = useState(false);
  const [busy, setBusy] = useState(false);
  const { card, currentCol } = move;

  const pick = async (colKey, sendInvoice = false) => {
    if (colKey === 'invoice_sent' && !confirmInvoice && !card.payment_received_at && currentCol !== 'invoice_sent') {
      setConfirmInvoice(true);
      return;
    }
    setBusy(true);
    await onMove(card.proposal_id, colKey, sendInvoice);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="move-sheet">
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="absolute bottom-0 left-0 right-0 bg-[#0C0C0C] border-t border-white/10 rounded-t-3xl px-5 pt-5 pb-10 max-w-lg mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 rounded-full bg-white/15 mx-auto mb-5" />
        {!confirmInvoice ? (
          <>
            <p className="text-white text-lg font-black tracking-tight" style={FONT_H}>{card.client_name}</p>
            <p className="text-zinc-600 text-xs mb-5" style={FONT_B}>Move to a different stage</p>
            <div className="space-y-1.5">
              {columns.filter(c => c.key !== currentCol).map(c => (
                <button key={c.key} onClick={() => pick(c.key)} disabled={busy}
                        className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl bg-white/[0.03] active:scale-[0.98] disabled:opacity-40 transition-transform text-left"
                        style={FONT_B} data-testid={`move-${card.proposal_id}-to-${c.key}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${COL_DOT[c.key]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-[14px] font-bold">{c.label}</p>
                    <p className="text-zinc-600 text-[11px] truncate">{COL_HINT[c.key]}</p>
                  </div>
                  {c.key === 'invoice_sent' && <Send className="w-4 h-4 text-zinc-600 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div data-testid="send-invoice-confirm">
            <p className="text-white text-lg font-black tracking-tight" style={FONT_H}>Send the invoice?</p>
            <p className="text-zinc-500 text-[13px] mt-2 mb-6 leading-relaxed" style={FONT_B}>
              Moving <span className="text-white font-bold">{card.client_name}</span> to Invoice Sent can also text them
              their invoice ({fmt(card.total)}) with the payment link — so this stage means what it says.
            </p>
            <div className="space-y-2.5">
              <button onClick={() => pick('invoice_sent', true)} disabled={busy}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform"
                      style={FONT_B} data-testid="confirm-send-invoice">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Invoice & Move
              </button>
              <button onClick={() => pick('invoice_sent', false)} disabled={busy}
                      className="w-full py-3.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform"
                      style={FONT_B} data-testid="confirm-move-only">
                Move only — already sent it myself
              </button>
              <button onClick={onClose} className="w-full py-3 text-zinc-600 text-xs font-bold" style={FONT_B} data-testid="confirm-cancel">
                Cancel
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export const PipelineBoard = ({ onOpenProposal }) => {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [active, setActive] = useState(0);
  const [move, setMove] = useState(null);
  const knownIds = useRef(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/pipeline');
      setColumns(data.columns);
      const ids = new Set(data.columns.flatMap(c => c.cards.map(x => x.proposal_id)));
      if (knownIds.current) {
        const fresh = [...ids].filter(id => !knownIds.current.has(id));
        if (fresh.length > 0) {
          setNewCount(n => n + fresh.length);
          toast.success(`${fresh.length} new lead${fresh.length > 1 ? 's' : ''} just came in!`);
        }
      }
      knownIds.current = ids;
    } catch (err) {
      console.error('Failed to load pipeline', err);
      toast.error('Failed to load pipeline');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const handleMove = async (proposalId, columnKey, sendInvoice = false) => {
    try {
      const res = await api.put(`/api/admin/pipeline/${proposalId}/move`, { column: columnKey, send_invoice: sendInvoice });
      toast.success(res.invoice_queued ? 'Invoice queued to send · moved' : 'Moved');
      setMove(null);
      load();
    } catch (err) {
      toast.error(err.message || 'Move failed');
      setMove(null);
    }
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || !el.children.length) return;
    const w = el.children[0].offsetWidth + 16;
    setActive(Math.min(columns.length - 1, Math.max(0, Math.round(el.scrollLeft / w))));
  };

  const scrollTo = (idx) => {
    const el = scrollRef.current;
    if (!el || !el.children.length) return;
    const w = el.children[0].offsetWidth + 16;
    el.scrollTo({ left: idx * w, behavior: 'smooth' });
  };

  if (loading) return <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading pipeline…</p>;

  return (
    <div data-testid="pipeline-board">
      {/* Stage rail — tap to jump, shows where you are */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="pipeline-rail">
          {columns.map((col, i) => (
            <button key={col.key} onClick={() => scrollTo(i)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-colors ${
                      i === active ? 'bg-white text-black' : 'bg-white/5 text-zinc-500'
                    }`} style={FONT_B} data-testid={`rail-${col.key}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${COL_DOT[col.key]}`} />
              {col.label}{col.count > 0 ? ` ${col.count}` : ''}
            </button>
          ))}
        </div>
        <button onClick={load} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 active:scale-95 flex-shrink-0 transition-transform" data-testid="pipeline-refresh" aria-label="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {newCount > 0 && (
        <button onClick={() => { setNewCount(0); scrollTo(0); }}
                className="mb-4 px-3 py-1 rounded-full bg-white text-black text-[10px] font-black tracking-wide animate-pulse"
                data-testid="new-leads-badge">
          +{newCount} NEW
        </button>
      )}

      <div ref={scrollRef} onScroll={onScroll}
           className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory -mx-5 px-5 no-scrollbar">
        {columns.map(col => (
          <div key={col.key} className="flex-shrink-0 w-[88vw] max-w-[400px] sm:w-96 snap-center" data-testid={`pipeline-col-${col.key}`}>
            <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-3xl p-4 min-h-[420px]">
              <div className="px-1 pb-4 flex items-start justify-between">
                <div>
                  <p className="text-white text-[20px] font-black tracking-tight flex items-center gap-2" style={FONT_H}>
                    <span className={`w-2 h-2 rounded-full ${COL_DOT[col.key] || 'bg-zinc-600'}`} />
                    {col.label}
                  </p>
                  <p className="text-zinc-600 text-[11px] mt-1 leading-snug" style={FONT_B}>{COL_HINT[col.key]}</p>
                </div>
                <p className="text-zinc-500 text-xs tabular-nums font-bold flex-shrink-0 mt-1" style={FONT_B}>{col.count} · {fmt(col.value)}</p>
              </div>
              <div className="space-y-2.5 max-h-[56vh] overflow-y-auto pb-1">
                {col.cards.length === 0 && (
                  <div className="border border-dashed border-white/10 rounded-2xl py-10 text-center">
                    <p className="text-zinc-700 text-xs" style={FONT_B}>Nothing here</p>
                  </div>
                )}
                {col.cards.map(card => (
                  <PipelineCard key={card.proposal_id} card={card} currentCol={col.key}
                                onMoveRequest={(c, cc) => setMove({ card: c, currentCol: cc })}
                                onOpen={onOpenProposal} />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Swipe affordance */}
      <div className="flex items-center justify-center gap-4 mt-4" data-testid="pipeline-dots">
        <button onClick={() => scrollTo(Math.max(0, active - 1))} disabled={active === 0}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 disabled:opacity-25 active:scale-95 transition-transform" data-testid="pipeline-prev" aria-label="Previous stage">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          {columns.map((col, i) => (
            <button key={col.key} onClick={() => scrollTo(i)} aria-label={col.label}
                    className={`rounded-full transition-all ${i === active ? `w-5 h-1.5 ${COL_DOT[col.key]}` : 'w-1.5 h-1.5 bg-white/15'}`} />
          ))}
        </div>
        <button onClick={() => scrollTo(Math.min(columns.length - 1, active + 1))} disabled={active === columns.length - 1}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 disabled:opacity-25 active:scale-95 transition-transform" data-testid="pipeline-next" aria-label="Next stage">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <p className="text-zinc-700 text-[10px] text-center mt-2 uppercase tracking-[0.2em] font-semibold" style={FONT_B}>Swipe between stages</p>

      <AnimatePresence>
        {move && <MoveSheet move={move} columns={columns} onClose={() => setMove(null)} onMove={handleMove} />}
      </AnimatePresence>
    </div>
  );
};
